import React from 'react';
import {
  ArrowRight,
  Bot,
  CalendarDays,
  ClipboardCheck,
  Code2,
  FileSearch,
  FileText,
  ListChecks,
  PhoneCall,
  Search,
  UserCheck,
  Users,
  Video,
} from 'lucide-react';
import { Button } from '@/Components/ui/button';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import useSEO from '../HomePage/useSEO';
import ScrollReveal from '../HomePage/ScrollReveal';
import {
  CapabilityGrid,
  DEMO_PATH,
  IndustryFlowStyles,
  IndustryWorkflow,
  Section,
  SectionIntro,
} from '../Industries/industryUi';

const RPO_OPERATING_STAGES = [
  {
    step: '01',
    title: 'Build the pipeline',
    icon: Search,
    items: [
      { title: 'Sourcing', detail: 'AI + recruiters', icon: Search },
      { title: 'Resume screening', detail: 'AI', icon: FileSearch },
      { title: 'Candidate engagement', detail: 'AI voice support', icon: PhoneCall },
    ],
  },
  {
    step: '02',
    title: 'Evaluate and move',
    icon: ClipboardCheck,
    items: [
      { title: 'Assessments', detail: 'AI', icon: ClipboardCheck },
      { title: 'Interviews', detail: 'AI + human', icon: Video },
      { title: 'Scheduling', detail: 'Automated', icon: CalendarDays },
    ],
  },
  {
    step: '03',
    title: 'Run the program',
    icon: UserCheck,
    humanLed: true,
    items: [
      { title: 'Candidate tracking', detail: 'Centralized', icon: ListChecks },
      { title: 'Reporting', detail: 'Hiring updates', icon: FileText },
      { title: 'Hiring decisions', detail: 'Human-led', icon: UserCheck, emphasis: true },
    ],
  },
];

const SCALE_WORKFLOW = [
  { title: 'Hiring demand grows', detail: 'More roles and candidate conversations enter the hiring path.', icon: Users },
  { title: 'AI handles repetitive work', detail: 'Screening, assessments, scheduling, and candidate evidence stay organized.', icon: Bot },
  { title: 'Recruiters manage higher-value work', detail: 'Recruiters focus on context, candidate conversations, and stakeholder alignment.', icon: FileText },
  { title: 'Human-led hiring decisions', detail: 'Your recruiters and hiring managers decide who to hire.', icon: UserCheck },
];

const IDEAL_FOR = [
  '20–50 hires',
  '50–200 hires',
  'GCC setup',
  'New business expansion',
  'Seasonal hiring',
  'Rapid startup growth',
  'Enterprise hiring programs',
];

const AI_WORK = [
  'Screening',
  'Candidate engagement',
  'Assessments',
  'Scheduling',
  'Candidate evidence',
];

const HUMAN_WORK = [
  'Recruiter oversight',
  'Account management',
  'Judgment',
  'Hiring decisions',
];

const CAPABILITIES = [
  {
    title: 'AI Sourcing & Screening',
    helps: 'Find relevant candidates and review resume fit against the role.',
    href: '/hirewithus/ai-sourcing-screening',
    icon: Search,
  },
  {
    title: 'AI Call Screening',
    helps: 'Run structured role-question calls before recruiter review.',
    href: '/hirewithus/ai-call-screening',
    icon: PhoneCall,
  },
  {
    title: 'Screening Assessment',
    helps: 'Score candidates against the job description before the next step.',
    href: '/hirewithus/screening-assessment',
    icon: ClipboardCheck,
  },
  {
    title: 'Functional Assessment',
    helps: 'Review role-based skills, communication, and behaviour.',
    href: '/hirewithus/functional-assessment',
    icon: Users,
  },
  {
    title: 'Coding Assessment',
    helps: 'Add technical assessment evidence for engineering roles.',
    href: '/hirewithus/coding-assessment',
    icon: Code2,
  },
  {
    title: 'Virtual Hiring',
    helps: 'Use structured AI interviews that your team can review.',
    href: '/hirewithus/virtualhiring',
    icon: Video,
  },
];

export default function RpoHiring() {
  useSEO({
    title: 'AI-Powered RPO Recruitment Services | SearchJob.ai',
    description:
      'Scale recruitment with dedicated recruiters, AI hiring agents, automation, assessments, interviews and analytics.',
    canonical: 'https://searchjob.ai/solutions/rpo',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Header />
      <main className="pt-16">
        <Hero />
        <ScrollReveal>
          <RpoOperatingModel />
        </ScrollReveal>
        <ScrollReveal>
          <ScaleWithoutHeadcount />
        </ScrollReveal>
        <ScrollReveal>
          <IdealFor />
        </ScrollReveal>
        <ScrollReveal>
          <AiAndHuman />
        </ScrollReveal>
        <ScrollReveal>
          <RelevantCapabilities />
        </ScrollReveal>
      </main>
      <Footer />
      <IndustryFlowStyles />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
      </div>
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 pt-12 pb-16 sm:px-6 lg:grid-cols-2 lg:gap-14 lg:px-8 lg:pt-16">
        <div className="text-center lg:text-left">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <Bot className="h-3.5 w-3.5" aria-hidden="true" />
            AI-powered RPO
          </p>
          <h1 className="mb-6 text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-[3.4rem] xl:text-6xl">
            Your Recruitment Team, <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">Multiplied by AI.</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl lg:mx-0">
            A dedicated recruitment team with an AI workforce behind every recruiter. SearchJob.ai combines recruiters, account management, and AI hiring agents into one extended Talent Acquisition function.
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
            <Button
              asChild
              size="lg"
              className="!h-12 !rounded-full !bg-primary px-8 text-base font-semibold !text-primary-foreground shadow-lg shadow-primary/25 group hover:!bg-primary/90"
            >
              <a href={DEMO_PATH}>
                Build Your RPO Team
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="!h-12 !rounded-full bg-card/70 px-8 text-base font-semibold backdrop-blur-md">
              <a href="#rpo-workflow">See RPO Workflow</a>
            </Button>
          </div>
        </div>
        <RpoDesk />
      </div>
    </section>
  );
}

function RpoDesk() {
  const team = [
    { label: 'AI workforce', note: 'Handles repeatable hiring tasks', icon: Bot },
    { label: 'Dedicated recruiters', note: 'Review candidates and guide the process', icon: Users },
    { label: 'Your hiring team', note: 'Makes the final hiring decisions', icon: UserCheck },
  ];

  return (
    <aside className="relative self-start" aria-label="AI-powered RPO team model">
      <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[3rem] bg-gradient-to-tr from-primary/20 via-emerald-500/10 to-teal-500/20 blur-3xl" />
      <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">One extended talent acquisition team</p>
      <ol className="space-y-3">
        {team.map((item, index) => (
          <li key={item.label}>
            <div className="flex items-start gap-3 rounded-2xl border border-primary bg-primary/5 px-4 py-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
                <item.icon className="h-4 w-4" aria-hidden="true" />
              </span>
              <span>
                <span className="block text-sm font-semibold text-foreground">{item.label}</span>
                <span className="mt-0.5 block text-sm leading-relaxed text-muted-foreground">{item.note}</span>
              </span>
            </div>
            {index < team.length - 1 ? <span className="mx-auto my-1 block h-4 w-px bg-primary/40" aria-hidden="true" /> : null}
          </li>
        ))}
      </ol>
    </aside>
  );
}

function RpoOperatingModel() {
  return (
    <Section compact muted>
      <SectionIntro
        fullWidth
        eyebrow="RPO operating model"
        title="One team, with the right work handled at the right level."
        copy="AI supports the repeatable steps. Recruiters and hiring managers stay responsible for context and decisions."
      />
      <ol className="grid gap-4 lg:grid-cols-3 lg:gap-5" aria-label="RPO operating model">
        {RPO_OPERATING_STAGES.map((stage) => (
          <li key={stage.step}>
            <article
              className={`h-full overflow-hidden rounded-2xl border bg-primary/5 transition duration-200 hover:-translate-y-0.5 hover:shadow-md motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-none ${
                stage.humanLed ? 'border-primary' : 'border-primary/30 hover:border-primary/45'
              }`}
            >
              <header className={`flex items-center gap-3 border-b px-5 py-4 ${stage.humanLed ? 'border-primary/20 bg-primary/[0.05]' : 'border-border bg-muted/30'}`}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
                  <stage.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-primary">Stage {stage.step}</span>
                  <h3 className="mt-0.5 text-base font-semibold text-foreground">{stage.title}</h3>
                </span>
              </header>
              <ul className="divide-y divide-border">
                {stage.items.map((item) => (
                  <li key={item.title} className={`flex items-center gap-3 px-5 py-4 ${item.emphasis ? 'bg-primary/[0.04]' : ''}`}>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground/70">
                      <item.icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-foreground">{item.title}</span>
                      <span className="mt-0.5 block text-sm text-muted-foreground">{item.detail}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </article>
          </li>
        ))}
      </ol>
    </Section>
  );
}

function ScaleWithoutHeadcount() {
  return (
    <Section compact id="rpo-workflow">
      <SectionIntro
        eyebrow="Scale without scaling headcount"
        title="Scale hiring without scaling headcount."
        copy="As hiring demand grows, routine work stays with AI so recruiters can stay close to the work that needs people."
      />
      <IndustryWorkflow steps={SCALE_WORKFLOW} plainDetail />
    </Section>
  );
}

function IdealFor() {
  return (
    <Section compact muted>
      <SectionIntro
        fullWidth
        eyebrow="Ideal for"
        title="Recruitment support that can meet the hiring moment."
        copy="Choose an RPO team when hiring demand needs dedicated recruiter capacity and a consistent process."
      />
      <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3" aria-label="Ideal RPO scenarios">
        {IDEAL_FOR.map((item) => (
          <li key={item} className="flex items-center gap-3 text-sm font-semibold text-foreground">
            <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function AiAndHuman() {
  return (
    <Section compact>
      <SectionIntro
        fullWidth
        eyebrow="AI and human control"
        title="AI supports the team. People lead the hiring decisions."
        copy="SearchJob.ai helps recruiters organize hiring work and evidence; it does not make the hiring decision."
      />
      <article className="overflow-hidden rounded-2xl border border-primary bg-primary/5">
        <header className="border-b border-primary/15 bg-primary/[0.045] px-5 py-4 sm:px-6">
          <p className="text-sm font-semibold text-foreground">Shared hiring workflow</p>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            AI handles repeatable work. Your team owns judgment and hiring decisions.
          </p>
        </header>
        <div className="grid md:grid-cols-2">
          <WorkList
            title="AI supports"
            description="Repeatable work stays organized and ready for recruiter review."
            items={AI_WORK}
          />
          <WorkList
            title="Your team leads"
            description="Recruiters and hiring managers remain responsible for the decision."
            items={HUMAN_WORK}
            separated
          />
        </div>
      </article>
    </Section>
  );
}

function WorkList({ title, description, items, separated = false }) {
  return (
    <section className={`p-5 sm:p-6 ${separated ? 'border-border border-t md:border-l md:border-t-0' : ''}`}>
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{description}</p>
      <ul className="mt-4 space-y-2.5 text-base leading-relaxed text-foreground">
        {items.map((item) => (
          <li key={item} className="flex gap-3">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RelevantCapabilities() {
  return (
    <Section compact muted>
      <SectionIntro
        eyebrow="Relevant capabilities"
        title="Products already on SearchJob.ai."
        copy="Use the capabilities that fit the roles and workflow your RPO team is running."
      />
      <CapabilityGrid items={CAPABILITIES} highlighted />
    </Section>
  );
}

